import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import NewsCard from '@/components/base/NewsCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { useSeo } from '@/hooks/useSeo';
import { news, newsCategories } from '@/mocks/news';

const PAGE_SIZE = 5;

export default function NewsList() {
  const [params, setParams] = useSearchParams();
  const category = params.get('category') ?? '全部';
  const page = Math.max(1, Number(params.get('page') ?? '1'));

  useSeo({
    title: '影视资讯 - 夏目彩春最新动态',
    description:
      '夏目彩春最新影视资讯汇总，按时间倒序更新作品进展、活动信息与行业报道，第一时间掌握她的最新动态。',
    keywords: '夏目彩春资讯,影视新闻,作品更新,活动信息,最新动态',
  });

  const filtered = useMemo(() => {
    const list = news.filter((n) => category === '全部' || n.category === category);
    return list.sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const setCategory = (cat: string) => {
    const next = new URLSearchParams();
    if (cat !== '全部') next.set('category', cat);
    setParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tabs = ['全部', ...newsCategories];

  return (
    <div className="bg-background-50">
      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-8 md:py-12">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '资讯' }]} />

        <div className="mt-5 mb-8">
          <span className="font-label text-xs tracking-[0.25em] uppercase text-accent-600">News & Updates</span>
          <h1 className="font-heading font-black text-3xl md:text-4xl text-foreground-950 mt-2">影视资讯</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl">
            按时间倒序整理夏目彩春相关影视资讯，涵盖作品更新、活动信息与行业报道，帮助你及时了解她的最新动态。
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-7">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setCategory(tab)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                category === tab
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:text-primary-600 border border-background-200/80'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="flex flex-col gap-5">
              {pageItems.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination page={current} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <EmptyState
            icon="ri-newspaper-line"
            title="该分类下暂时没有资讯"
            description="试着切换到其他资讯类型，或查看全部最新动态。"
            actionLabel="查看全部资讯"
            onAction={() => setCategory('全部')}
          />
        )}
      </div>
    </div>
  );
}